/* ──────────────────────────────────────────────
 *  Codes du parcours hors du tunnel : ceux d'un lien vers le
 *  tunnel (`?code=`, `?promo=`), et ceux gardés par la visite
 *  (panier unique) tels que la barre de panier et la fiche du
 *  site les passent au résumé du panier et au lien « Choisir
 *  un créneau ». Le tunnel essaie chaque code apporté par le
 *  champ unique (`code` du résumé : le serveur tranche sa
 *  nature), quel que soit le paramètre qui le porte.
 * ────────────────────────────────────────────── */

import { VOUCHER_CODE_QUERY_PARAM, type CartSummaryRequestObject } from '@cfixe-monorepo/shared';
import type { BookingCartCodes } from './booking-cart-store';

/** Paramètre de requête d'un code promo de campagne (`/book/{slug}?promo=…`, page de réservation du site). */
export const PROMO_CODE_QUERY_PARAM = 'promo';

/** Codes portés par l'adresse d'une page du tunnel. */
export interface LinkCodes {
  /** `?code=` : QR d'une carte cadeau, lien de campagne, « Réserver avec le code » d'une promotion du site. */
  code?: string;
  /** `?promo=` : code promo d'une campagne. */
  promo?: string;
}

/** Codes de l'adresse (`search` : `?code=…&promo=…`, comme `window.location.search`). */
export function readLinkCodes(search: string): LinkCodes {
  const params = new URLSearchParams(search);
  const code = params.get(VOUCHER_CODE_QUERY_PARAM)?.trim();
  const promo = params.get(PROMO_CODE_QUERY_PARAM)?.trim();
  return { ...(code ? { code } : {}), ...(promo ? { promo } : {}) };
}

/**
 * Paramètres du lien vers le tunnel qui portent les codes de la visite (ajoutés à `?mode=booking`) : le code
 * promo posé sous `promo`, la carte cadeau — sinon le premier code encore en attente — sous `code`. Le tunnel
 * relit de toute façon le panier de la visite à l'ouverture : l'adresse n'est qu'un rappel.
 */
export function linkCodesQuery(codes: BookingCartCodes | undefined): Record<string, string> {
  if (!codes) return {};
  const code = codes.voucher ?? codes.pending?.[0];
  return {
    ...(codes.promo ? { [PROMO_CODE_QUERY_PARAM]: codes.promo } : {}),
    ...(code ? { [VOUCHER_CODE_QUERY_PARAM]: code } : {}),
  };
}

/**
 * Codes de la visite tels que `cart-summary` les attend : les codes posés par leur nature, le premier code
 * encore en attente par le champ unique `code` (le serveur dit s'il le retient). Les montants restent chiffrés
 * par le serveur.
 */
export function cartSummaryCodesParams(
  codes: BookingCartCodes | undefined,
): Pick<CartSummaryRequestObject, 'promo_code' | 'voucher_code' | 'code'> {
  if (!codes) return {};
  const pending = codes.pending?.[0];
  return {
    ...(codes.promo ? { promo_code: codes.promo } : {}),
    ...(codes.voucher ? { voucher_code: codes.voucher } : {}),
    ...(pending ? { code: pending } : {}),
  };
}
